/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ASTNode, DocumentNode, visit } from 'graphql';
import isNil from 'lodash/isNil';

const omitFieldNames = ['__typename'];

type ParentNode = ASTNode | readonly ASTNode[] | undefined;

const getPermissionsFromAncestors = (
  field: ParentNode,
  ancestors: readonly (ASTNode | readonly ASTNode[])[],
  permissions: any,
) => {
  const fieldName = getName(field);
  const allAncestorNames = ancestors
    .filter((node) => isFieldType(node))
    .map((node) => getName(node));

  return getPermissions([...allAncestorNames, fieldName], permissions);
};

const getPermissions = (fieldNames: string[], permissions: NonNullable<any>): [any, string] => {
  if (fieldNames.length === 1) {
    // is Query Resolver -> return permissions for underlying type and underlying type name
    return [permissions[permissions[fieldNames[0]]], permissions[fieldNames[0]]];
  }

  let ancestorPermissions: any = undefined;
  for (const ancestor of fieldNames) {
    const permissionValue = !ancestorPermissions
      ? permissions[ancestor]
      : ancestorPermissions[ancestor];

    if (permissionValue === false) {
      return [false, ancestor];
    }

    ancestorPermissions = permissions[permissionValue];
  }

  return [ancestorPermissions, fieldNames[fieldNames.length - 2]];
};

// @ts-ignore
const getKind = (node: ParentNode) => node?.kind;
// @ts-ignore
const getName = (node: ParentNode) => node?.name?.value;
const isFieldType = (node: ParentNode) => getKind(node) === 'Field';

export class PermissionsTransformer {
  onTransform: ((message: string) => void) | undefined;
  permissions: unknown;

  constructor(permissions: unknown, onTransform?: (message: string) => void) {
    this.permissions = permissions;
    this.onTransform = onTransform;
  }

  transform(document: DocumentNode): DocumentNode {
    const { onTransform, permissions } = this;

    if (!permissions) {
      return document;
    }

    return visit(document, {
      SelectionSet: {
        enter(node, _key, parentField, _path, ancestors) {
          const parentKind = getKind(parentField);
          if (parentKind === 'OperationDefinition') {
            return node;
          }

          const [entityPermission, ancestor] = getPermissionsFromAncestors(
            parentField,
            ancestors,
            permissions,
          );

          if (isNil(entityPermission)) {
            return;
          }

          if (!entityPermission) {
            return null; // delete this node and all children
          }

          node.selections = node.selections.filter((selection) => {
            if (!isFieldType(selection)) {
              return true; // keep field in the request
            }

            const fieldName = getName(selection);
            const permission = entityPermission?.[fieldName] ?? entityPermission?.all;

            if (omitFieldNames.includes(fieldName) || !!permission) {
              return true; // keep field in the request
            }

            onTransform?.(`Removing field: ${fieldName} from: ${ancestor}`);
            return false; // delete this field from the request
          });

          if (!node.selections.length) {
            return null; // delete this entire node from the request
          }

          return node;
        },
        leave(node, _key, parentField, _path, ancestors) {
          if (node.selections.length === 0) {
            return null;
          }

          const [entityPermission] = getPermissionsFromAncestors(
            parentField,
            ancestors,
            permissions,
          );

          if (isNil(entityPermission)) {
            return;
          }

          const newSelections = node.selections.filter((selection: any) => {
            const fieldName = getName(selection);
            const hasSelection = !!selection.selectionSet;
            const isEntity = typeof entityPermission?.[fieldName] === 'string';

            return isEntity ? hasSelection : true;
          });

          if (node.selections.length === 0) {
            return null;
          }

          node.selections = newSelections;
          return node;
        },
      },
    });
  }
}
